import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Searchable Jira source picker: pick a Project, an issue Type (Epic/Story/…)
// and then search + select a specific issue. Used by the "Build from scratch"
// flow so an app can be generated from an existing Jira Epic/Story.
//
// Props:
//   apiUrl              base URL for API calls
//   authenticatedFetch  fetch wrapper that attaches the Okta bearer token
//   value               { projectKey, issueType, issueKey, issueSummary }
//   onChange(next)      called with the merged next value on any change
//
// Backed by:
//   GET /api/projects                                   → { projects:[{key,name}], requiresAuth?, authUrl? }
//   GET /api/search-issues?projectKey=&query=           → { issues:[{key,fields:{summary,issuetype,status}}] }

const ISSUE_TYPES = ['Epic', 'Story', 'Task', 'Bug', 'Any'];
const MAX_RESULTS = 25;

const s = {
  wrap: { border: '1px solid #3a3a4a', borderRadius: 8, padding: 12, backgroundColor: '#191922', marginBottom: 16 },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  collapseBtn: { background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 14, padding: 4, lineHeight: 1 },
  toggle: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' },
  bodyOff: { opacity: 0.4, pointerEvents: 'none', filter: 'grayscale(0.4)' },
  head: { fontSize: 13, fontWeight: 700, color: '#ce93d8', marginBottom: 4 },
  sub: { fontSize: 12, color: '#999', marginBottom: 10 },
  label: { display: 'block', fontSize: 12, color: '#bbb', margin: '8px 0 4px' },
  select: { width: '100%', padding: '8px 10px', backgroundColor: '#1e1e1e', color: '#eee', border: '1px solid #444', borderRadius: 6, fontSize: 13 },
  input: { width: '100%', padding: '8px 10px', backgroundColor: '#1e1e1e', color: '#eee', border: '1px solid #444', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
  searchWrap: { position: 'relative' },
  dropdown: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, maxHeight: 240, overflowY: 'auto', border: '1px solid #555', borderRadius: 6, backgroundColor: '#161620', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' },
  row: (active) => ({ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #262633', backgroundColor: active ? '#2d1f3d' : 'transparent', display: 'flex', gap: 8, alignItems: 'baseline' }),
  key: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, color: '#7cc4ff', flexShrink: 0 },
  summ: { fontSize: 12, color: '#ddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  status: { fontSize: 10, color: '#888', marginLeft: 'auto', flexShrink: 0 },
  chip: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '8px 10px', backgroundColor: '#20182b', border: '1px solid #6b4a80', borderRadius: 6 },
  chipKey: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, color: '#ce93d8', fontWeight: 700 },
  chipSumm: { fontSize: 12, color: '#ddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  clearBtn: { marginLeft: 'auto', background: 'none', border: 'none', color: '#ef9a9a', cursor: 'pointer', fontSize: 12 },
  hint: { fontSize: 11, color: '#888', marginTop: 6 },
  err: { fontSize: 12, color: '#ef9a9a', marginTop: 6 },
  connectBtn: { display: 'inline-block', marginTop: 6, padding: '6px 12px', backgroundColor: '#1a2030', color: '#7cc4ff', border: '1px solid #4fc3f7', borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' },
};

export default function JiraIssuePicker({
  apiUrl,
  authenticatedFetch,
  value,
  onChange,
  enabled = false,
  onToggle,
  title = '🔗 Build from a Jira issue (optional)',
  subtitleOn = 'Pick a project and an Epic/Story to base this app on.',
  subtitleOff = 'Off — this app builds from your description below. Check the box to base it on a Jira Epic/Story.',
}) {
  const val = useMemo(() => value || {}, [value]);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [error, setError] = useState('');

  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [focused, setFocused] = useState(false);
  const blurRef = useRef(0);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectFocused, setProjectFocused] = useState(false);
  const projBlurRef = useRef(0);
  const [open, setOpen] = useState(false);

  const patch = useCallback((next) => onChange({ ...val, ...next }), [onChange, val]);

  // Load projects once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setProjectsLoading(true);
      setError('');
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/projects`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data && data.requiresAuth) {
          setRequiresAuth(true);
          setAuthUrl(data.authUrl || '');
          return;
        }
        if (res.ok && data.success) {
          const list = (data.projects || [])
            .map((p) => ({ key: p.key, name: p.name || p.key }))
            .sort((a, b) => a.key.localeCompare(b.key));
          setProjects(list);
        } else {
          setError(data.error || data.message || 'Failed to load Jira projects.');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load Jira projects.');
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  // Debounced issue search whenever project / search text changes.
  const debounceRef = useRef(0);
  useEffect(() => {
    if (!val.projectKey) { setIssues([]); return undefined; }
    let cancelled = false;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIssuesLoading(true);
      setError('');
      try {
        const q = search.trim();
        const url = `${apiUrl}/api/search-issues?projectKey=${encodeURIComponent(val.projectKey)}${q ? `&query=${encodeURIComponent(q)}` : ''}`;
        const res = await authenticatedFetch(url);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success) {
          const mapped = (data.issues || []).map((i) => ({
            key: i.key,
            summary: i.fields?.summary || '',
            type: i.fields?.issuetype?.name || '',
            status: i.fields?.status?.name || '',
          }));
          setIssues(mapped);
        } else {
          setIssues([]);
          setError(data.error || data.message || 'Failed to search Jira issues.');
        }
      } catch (err) {
        if (!cancelled) { setIssues([]); setError(err.message || 'Failed to search Jira issues.'); }
      } finally {
        if (!cancelled) setIssuesLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, val.projectKey, search]);

  const typeFilter = val.issueType || 'Epic';
  const visibleIssues = issues.filter(
    (i) => typeFilter === 'Any' || (i.type || '').toLowerCase() === typeFilter.toLowerCase(),
  );

  const selectedProject = projects.find((p) => p.key === val.projectKey);
  const selectedProjectLabel = selectedProject ? `${selectedProject.key} — ${selectedProject.name}` : (val.projectKey || '');
  const pq = projectSearch.trim().toLowerCase();
  const filteredProjects = pq
    ? projects.filter((p) => p.key.toLowerCase().includes(pq) || (p.name || '').toLowerCase().includes(pq))
    : projects;

  return (
    <div style={s.wrap}>
      <div style={s.headerRow}>
        <label style={s.toggle}>
          <input
            type="checkbox"
            checked={!!enabled}
            onChange={(e) => { const c = e.target.checked; if (c) setOpen(true); onToggle && onToggle(c); }}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          <span style={s.head}>{title}</span>
        </label>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={s.collapseBtn}
          title={open ? 'Collapse' : 'Expand'}
          aria-label={open ? 'Collapse Jira section' : 'Expand Jira section'}
        >
          {open ? '▾' : '▸'}
        </button>
      </div>

      {open && (enabled && requiresAuth ? (
        <div style={{ marginTop: 8 }}>
          <div style={s.sub}>Your Jira account isn’t connected yet.</div>
          {authUrl
            ? <a style={s.connectBtn} href={authUrl} target="_blank" rel="noopener noreferrer">Connect Jira →</a>
            : <div style={s.err}>Jira is not configured. Ask an admin to enable the Jira integration.</div>}
        </div>
      ) : (
        <div style={enabled ? { marginTop: 8 } : { ...s.bodyOff, marginTop: 8 }}>
          <div style={s.sub}>
            {enabled ? subtitleOn : subtitleOff}
          </div>

          <label style={s.label}>Jira Project</label>
      <div
        style={s.searchWrap}
        onBlur={() => { clearTimeout(projBlurRef.current); projBlurRef.current = setTimeout(() => setProjectFocused(false), 150); }}
      >
        <input
          style={s.input}
          value={projectFocused ? projectSearch : selectedProjectLabel}
          disabled={projectsLoading}
          onFocus={() => { clearTimeout(projBlurRef.current); setProjectFocused(true); setProjectSearch(''); }}
          onChange={(e) => setProjectSearch(e.target.value)}
          placeholder={projectsLoading ? 'Loading projects…' : 'Search projects by key or name…'}
        />
        {projectFocused && (
          <div style={s.dropdown}>
            {filteredProjects.length === 0 ? (
              <div style={{ padding: 10, fontSize: 12, color: '#888' }}>
                No projects found{projectSearch ? ` for “${projectSearch}”` : ''}.
              </div>
            ) : (
              filteredProjects.slice(0, MAX_RESULTS).map((p) => (
                <div
                  key={p.key}
                  style={s.row(val.projectKey === p.key)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    patch({ projectKey: p.key, issueKey: '', issueSummary: '' });
                    setSearch('');
                    clearTimeout(projBlurRef.current);
                    setProjectFocused(false);
                  }}
                >
                  <span style={s.key}>{p.key}</span>
                  <span style={s.summ}>{p.name}</span>
                </div>
              ))
            )}
            {filteredProjects.length > MAX_RESULTS && (
              <div style={{ padding: '6px 10px', fontSize: 11, color: '#888' }}>
                Showing first {MAX_RESULTS} of {filteredProjects.length} — keep typing to narrow.
              </div>
            )}
          </div>
        )}
      </div>

      {val.projectKey ? null : (
        <div style={{ fontSize: 11, color: '#888', margin: '6px 0 0' }}>
          Select a project above to choose an Epic/Story.
        </div>
      )}

      <label style={s.label}>Issue Type</label>
      <select
        style={s.select}
        value={val.issueType || 'Epic'}
        disabled={!val.projectKey}
        onChange={(e) => patch({ issueType: e.target.value })}
      >
        {ISSUE_TYPES.map((t) => (
          <option key={t} value={t}>{t === 'Any' ? 'Any type' : t}</option>
        ))}
      </select>

      <label style={s.label}>Search {typeFilter === 'Any' ? 'issues' : `${typeFilter} issues`}</label>

      {val.issueKey && !focused ? (
            <div style={s.chip}>
              <span style={s.chipKey}>{val.issueKey}</span>
              <span style={s.chipSumm}>{val.issueSummary}</span>
              <button
                type="button"
                style={s.clearBtn}
                onClick={() => { patch({ issueKey: '', issueSummary: '' }); setSearch(''); }}
              >
                ✕ clear
              </button>
            </div>
          ) : (
            <div
              style={s.searchWrap}
              onBlur={() => { clearTimeout(blurRef.current); blurRef.current = setTimeout(() => setFocused(false), 150); }}
            >
              <input
                style={s.input}
                value={search}
                disabled={!val.projectKey}
                onFocus={() => { clearTimeout(blurRef.current); setFocused(true); }}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={val.projectKey
                  ? `Type to search ${typeFilter === 'Any' ? 'issues' : `${typeFilter}s`} by key or summary…`
                  : 'Select a project first…'}
              />
              {focused && (
                <div style={s.dropdown}>
                  {issuesLoading ? (
                    <div style={{ padding: 10, fontSize: 12, color: '#888' }}>Searching…</div>
                  ) : visibleIssues.length === 0 ? (
                    <div style={{ padding: 10, fontSize: 12, color: '#888' }}>
                      No {typeFilter === 'Any' ? 'issues' : `${typeFilter} issues`} found{search ? ` for “${search}”` : ''}.
                    </div>
                  ) : (
                    <>
                      {visibleIssues.slice(0, MAX_RESULTS).map((i) => (
                        <div
                          key={i.key}
                          style={s.row(val.issueKey === i.key)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            patch({ issueKey: i.key, issueSummary: i.summary, issueType: i.type || val.issueType });
                            setSearch('');
                            clearTimeout(blurRef.current);
                            setFocused(false);
                          }}
                        >
                          <span style={s.key}>{i.key}</span>
                          <span style={s.summ}>{i.summary || '(no title)'}</span>
                          {i.status && <span style={s.status}>{i.status}</span>}
                        </div>
                      ))}
                      {visibleIssues.length > MAX_RESULTS && (
                        <div style={{ padding: '6px 10px', fontSize: 11, color: '#888' }}>
                          Showing first {MAX_RESULTS} of {visibleIssues.length} — keep typing to narrow.
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

      {error && <div style={s.err}>⚠️ {error}</div>}
        </div>
      ))}
    </div>
  );
}
